/**
 * SKE-4682 — every customer-facing string on the campaign page, in one file.
 *
 * One file so Hugh can review the copy in a single pass and so the vocabulary
 * rules can be tested against a single surface rather than hunted through a
 * dozen components. Nothing in `page/` writes a sentence of its own.
 *
 * WHERE EACH LINE CAME FROM. Lines marked BANK are verbatim from section 5 of
 * `docs/instagram-content-publishing/sked-m4-reconnect-campaign-strategy.md`.
 * Lines marked TICKET are verbatim from SKE-4682 or SKE-4687, which WIN
 * wherever they disagree with the bank, and they disagree in four places:
 *
 *   1. the bank's checklist rows carry a scheduled-post count; the ticket
 *      removed it, so no row here is numeric,
 *   2. the bank's wrong-person block has three buttons; SKE-4687 dropped
 *      "Notify your admins", so there are two,
 *   3. the bank's completion sentence says "in the composer", a banned
 *      phrase; it says "next time you create a post",
 *   4. the bank's banner lines predate the 2026-09-11 reconciliation; the
 *      banner's copy lives in `campaign-registers.ts` and is not repeated here.
 *
 * Lines marked DRAFT are neither source's: the page needs them and nobody has
 * written them yet. They follow the vocabulary rules below and every one of
 * them is listed in the handover for review.
 *
 * THE VOCABULARY, which no string in this file may break. The verb is UPDATE
 * and the button is "Update now". The word "reconnect" is banned, because
 * "Reconnect Account" meant the phone login elsewhere in the product and a
 * customer sent to that control had done nothing. That control is now "Update
 * phone login" and the Facebook one "Update Facebook permissions", so the
 * collision is gone, but the ban stands: the verb across all of this is UPDATE. Never say migration,
 * scope, permission set, token or "the composer"; say "when you create a
 * post". The lead angle is better publishing, never security or passwords.
 * `campaign-page-copy.test.ts` asserts all of it against the rendered page.
 *
 * THE COUNTS ARE NEVER BARE NUMBERS in a sentence that also carries a verb.
 * `accountCount` decides the plural, so "1 account will stop publishing" reads
 * correctly beside "4 accounts will stop publishing"; a template with a
 * hard-coded "accounts" gets the singular wrong on exactly the customer with
 * one account, who is most of them.
 */

import {
  HERO_VARIANT,
  accountCount,
  dayCount,
  formatCutover,
  type HeroVariant,
} from "./campaign-registers";

/** What the page needs in order to fill its counts and its date in. */
export interface CampaignPageCopyContext {
  /** ISO 8601 from the server, or null when no date is configured. */
  cutoverAt: string | null | undefined;
  /** The customer's PROFILE timezone (`user.timezone`), never the browser's. */
  timeZone?: string | null;
  /** Instagram accounts still to update. Never includes Facebook Pages. */
  outstanding: number;
  /** Whole days remaining, from the server. Never recomputed here. */
  daysRemaining?: number | null;
  /** Whole hours remaining, from the server. Week 3's final 72 only. */
  hoursRemaining?: number | null;
}

/** "1 hour" or "9 hours". The week 3 twin of `dayCount`. */
export function hourCount(hours: number | null | undefined): string {
  const value = typeof hours === "number" && hours > 0 ? hours : 0;
  return `${value} ${value === 1 ? "hour" : "hours"}`;
}

/* ------------------------------------------------------------------ hero */

export const PAGE_TITLE = "Update Instagram publishing"; // DRAFT

export const HERO = {
  /** BANK. Weeks 1 and 2. */
  eyebrow: "INSTAGRAM PUBLISHING",
  /**
   * BANK. The italic word is `upgrade`, and it is the page's WHOLE italic
   * budget: exactly one Tiempos Headline italic word, spent here, so the strip,
   * the checklist and the rail carry none. Split rather than written as one
   * string because the component wraps the middle part in `.highlight-serif`.
   */
  headlineBefore: "Instagram publishing is getting an ",
  headlineItalic: "upgrade",
  headlineAfter: ".",
  /** BANK. */
  body:
    "Sked Social now publishes to Instagram through Meta's publishing API. " +
    "Update your accounts to switch over, and you get a set of controls the " +
    "old way could never support.",
  /** BANK, and the load-bearing lines SKE-4682 calls out by name. */
  primary: "Update now",
  /**
   * DRAFT. The same button while a pass is running: disabled, and SAYING it is
   * working rather than just going grey. This is the state a fifty-account
   * agency looks at for about a minute.
   */
  primaryInProgress: "Updating…",
  takesAMinute: "Takes about a minute.",
  facebookPages: "This also refreshes your connected Facebook Pages.",
  /** BANK. */
  deadline: (context: CampaignPageCopyContext): string =>
    `Accounts not updated by ${formatCutover(context.cutoverAt, context.timeZone)} will stop publishing to Instagram.`,
} as const;

export const HERO_WEEK_3 = {
  /** TICKET (SKE-4682) and the banner's own week 3 flag. */
  flag: "Action required",
  /**
   * BANK, with the plural taken off the hard-coded "accounts". The bank writes
   * "{n} accounts will stop publishing on {cutover}."; a workspace with one
   * account would have read "1 accounts".
   */
  headline: (context: CampaignPageCopyContext): string =>
    `${accountCount(context.outstanding)} will stop publishing on ${formatCutover(context.cutoverAt, context.timeZone)}.`,
  /**
   * BANK for the plural. DRAFT for the singular, for the same reason: "Update
   * them now" is wrong in front of one account.
   */
  body: (context: CampaignPageCopyContext): string =>
    context.outstanding === 1
      ? "Update it now and it keeps publishing, with the new Instagram controls switched on."
      : "Update them now and they keep publishing, with the new Instagram controls switched on.",
  /** BANK. */
  secondary: "Talk to support",
} as const;

/** DRAFT. Week 3 replaces the hero mock with this. */
export const COUNTDOWN = {
  /**
   * Days through week 3, switching to HOURS inside the final 72. Both numbers
   * come from the server; neither is recomputed from the clock in the browser,
   * which would disagree with the banner on the hour a boundary is crossed.
   */
  value: (context: CampaignPageCopyContext): string =>
    context.hoursRemaining != null &&
    context.daysRemaining != null &&
    context.daysRemaining < 3
      ? hourCount(context.hoursRemaining)
      : dayCount(context.daysRemaining),
  label: "left to update",
  until: (context: CampaignPageCopyContext): string =>
    `Instagram publishing switches over on ${formatCutover(context.cutoverAt, context.timeZone)}.`,
} as const;

/* ---------------------------------------------------------- then vs now */

export interface ThenNowRow {
  id: "post-delays" | "story-sets" | "carousel-tags" | "reel-cover";
  headline: string;
  before: string;
  after: string;
  /** Row one only: the relaxed limit is per POST, so the footnote hangs off it. */
  footnoted?: true;
}

export const THEN_NOW = {
  heading: "What you get", // DRAFT
  benefitColumn: "Benefit", // DRAFT, screen readers only
  before: "BEFORE", // DRAFT
  afterYouUpdate: "AFTER YOU UPDATE",
  /** DRAFT, all four rows. */
  rows: [
    {
      id: "post-delays",
      headline: "No more waiting",
      before: "A 15-minute wait between posts.",
      after: "Posts go out the moment they're due.",
      footnoted: true,
    },
    {
      id: "story-sets",
      headline: "Ten stories at once",
      before: "Only 3 stories per set.",
      after: "Schedule a full set and they publish in sequence.",
    },
    {
      id: "carousel-tags",
      headline: "Tag every slide",
      before: "Tags stuck on slide 1.",
      after: "Tag people and products on each slide.",
    },
    {
      id: "reel-cover",
      headline: "Pick your reel cover",
      before: "The first frame became the cover.",
      after: "The frame you choose is the one that shows.",
    },
  ] as readonly ThenNowRow[],
  /**
   * DRAFT, and it is the honest half of row one. The relaxed limit is per
   * POST, not per account: a workspace still publishing a link-sticker story
   * from a phone keeps the old window on that post.
   */
  footnote:
    "Stories with link stickers still publish from a phone and keep the 15-minute gap.",
} as const;

/* ------------------------------------------------------------ capability */

export type CapabilityCardId =
  "trial-reels" | "trending-audio" | "reel-covers" | "per-slide-tagging";

export interface CapabilityCard {
  id: CapabilityCardId;
  title: string;
  caption: string;
}

/**
 * The four cards that exist, of which THREE render. Which three depends on
 * `HERO_VARIANT`; see `capabilityCards` below.
 */
export const CAPABILITY_CARDS: Record<CapabilityCardId, CapabilityCard> = {
  /** BANK. */
  "trial-reels": {
    id: "trial-reels",
    title: "Trial reels",
    caption: "Try a reel on non-followers first, then graduate it.",
  },
  /** BANK. */
  "trending-audio": {
    id: "trending-audio",
    title: "Trending audio",
    caption: "Pick a track from Instagram's audio catalogue.",
  },
  /**
   * DRAFT, and it exists only for the `reel_cover` variant. The bank has no
   * fourth card because the bank assumes audio ships.
   *
   * The caption deliberately echoes the then-vs-now strip's reel-cover row
   * rather than inventing a second way of saying it, because the two sit a
   * screen apart on the same page.
   */
  "reel-covers": {
    id: "reel-covers",
    title: "Reel covers",
    caption: "Choose the best cover frame.",
  },
  /** BANK. */
  "per-slide-tagging": {
    id: "per-slide-tagging",
    title: "Per-slide tagging",
    caption: "Tag each slide from the filmstrip.",
  },
};

/**
 * SKE-4688, leg two of the switch: the MIDDLE card changes with the variant.
 *
 * Trial reels and per-slide tagging are both proven live and are never in
 * question. The middle slot carries whatever the hero is showing, so the strip
 * and the hero cannot end up arguing for different capabilities.
 */
export function capabilityCards(
  variant: HeroVariant = HERO_VARIANT
): CapabilityCard[] {
  return [
    CAPABILITY_CARDS["trial-reels"],
    variant === "audio"
      ? CAPABILITY_CARDS["trending-audio"]
      : CAPABILITY_CARDS["reel-covers"],
    CAPABILITY_CARDS["per-slide-tagging"],
  ];
}

export const CAPABILITIES = {
  /** BANK, and the phrase that replaces the banned "the composer". */
  heading: "New when you create a post",
} as const;

/* ------------------------------------------------------------ completion */

/**
 * SKE-4688, leg three of the switch. Rendered by SKE-4687 in commit three;
 * the sentence lives here now so that the switch is complete in one commit
 * rather than half-built across two.
 *
 * BANK, with two corrections. The bank writes "…are now available in the
 * composer", and "the composer" is banned, so it says "next time you create a
 * post". And the list loses "trending audio" — and its Oxford-free comma
 * becomes an "and" — on the `reel_cover` variant, because a sentence that
 * promises a track picker the page no longer shows anywhere is the exact
 * failure this switch exists to prevent.
 *
 * Product tagging and location tagging are absent from both lists on purpose.
 * Neither is promised anywhere in the campaign: product tag rendering is
 * unverified and behind its own flag, and location tagging needs Page Public
 * Content Access, which Sked does not hold.
 */
export const COMPLETION = {
  ready: (handle: string, variant: HeroVariant = HERO_VARIANT): string =>
    variant === "audio"
      ? `${handle} is ready. Cover frames, per-slide tagging, story sets of 10 and trending audio are available next time you create a post.`
      : `${handle} is ready. Cover frames, per-slide tagging and story sets of 10 are available next time you create a post.`,

  /**
   * DRAFT. One line for the whole pass, whatever its size.
   *
   * `ready` is right for one account and wrong for fifty: an agency finishing a
   * large pass got fifty near-identical paragraphs differing only in a handle,
   * which is a wall of text at the one moment the campaign is finally saying
   * well done. The per-account detail has a better home already, because the
   * checklist rows carry each account's own state and stay on screen.
   *
   * So the summary names the capabilities once, the rows name the accounts, and
   * neither repeats the other. It stays switch-aware for the same reason
   * `ready` does: promising a track picker the page no longer shows is the
   * failure the hero switch exists to prevent.
   */
  summary: (updated: number, variant: HeroVariant = HERO_VARIANT): string => {
    const subject =
      updated === 1 ? "1 account is ready" : `${updated} accounts are ready`;
    return variant === "audio"
      ? `${subject}. Cover frames, per-slide tagging, story sets of 10 and trending audio are available next time you create a post.`
      : `${subject}. Cover frames, per-slide tagging and story sets of 10 are available next time you create a post.`;
  },
} as const;

/* ----------------------------------------------------------- mock strings */

/**
 * Every word inside the coded mocks, because a mock is still something a
 * customer reads.
 *
 * PRODUCT means the string is copied verbatim out of the real control, and
 * most of them are: "Publish as a trial reel" and its graduation choice come
 * from `InstagramTrialReel.tsx`, and "Search tracks", "Track volume" and
 * "Your video's volume" come from `InstagramAudioPicker.tsx`. A mock whose
 * labels do not match the product is a mock that teaches the customer the
 * wrong thing to look for.
 *
 * THE TRACKS ARE INVENTED, and deliberately. Real song titles and real artist
 * names in marketing chrome are a permissions question nobody has asked, and
 * the catalogue is Meta's rather than ours, so the mock uses plausible names
 * that are nobody's. They are not "Track 1" either: a placeholder in the hero
 * reads as a page that was never finished.
 *
 * The volumes are 70 and 30 from section 4 of the strategy document, not the
 * product's own default of 100 for both. A mock at 100/100 shows two full
 * sliders and demonstrates nothing.
 */
export const MOCKS = {
  /** PRODUCT. The button in the app says Create post, so the mock does. */
  createPost: "Create post",

  trialReel: {
    /** PRODUCT. */
    toggle: "Publish as a trial reel",
    /** PRODUCT. */
    graduationHeading: "How it graduates",
    /** PRODUCT, both options, with the product's own default selected. */
    graduationOptions: [
      "You share it with followers from the Instagram app",
      "Instagram shares it with followers if it performs well",
    ],
    selectedGraduationIndex: 0,
  },

  audio: {
    /** PRODUCT. */
    searchPlaceholder: "Search tracks",
    /** PRODUCT. */
    trackVolumeLabel: "Track volume",
    /** PRODUCT, apostrophe included. */
    videoVolumeLabel: "Your video's volume",
    /** Section 4 of the strategy document. */
    trackVolume: 70,
    videoVolume: 30,
    /** DRAFT, invented, see the note above. The FIRST one is selected. */
    tracks: [
      { title: "Golden Hour Drive", artist: "Marla Cruz", duration: "2:48" },
      { title: "Neon Saturdays", artist: "The Waiting Room", duration: "3:12" },
      { title: "Slow Coast", artist: "Ilya Baines", duration: "2:26" },
    ],
    selectedTrackIndex: 0,
  },

  reelCover: {
    /** DRAFT. The lime callout SKE-4688 asks for, by name. */
    callout: "Frame 2 is the cover",
    /** DRAFT. Six frames, and the SECOND is the chosen cover. */
    frameCount: 6,
    selectedFrameIndex: 1,
    /** DRAFT. The small card's version says the same thing shorter. */
    cardCallout: "Frame 2",
  },

  perSlideTagging: {
    /**
     * DRAFT. THREE slides, the second open, and one of them carrying nothing.
     *
     * Three rather than four because of width, not taste: at a 1024px viewport
     * the page's left column leaves each card about 158px inside its padding,
     * and a fourth tile does not fit without shrinking all of them past
     * legibility. Three still makes the claim, which is that tags land per
     * slide rather than all on slide 1.
     *
     * The counts are uneven and one is zero on purpose. Three slides each
     * carrying one tag would illustrate the OLD behaviour just as well.
     */
    tagCounts: [2, 0, 3],
    selectedSlideIndex: 1,
  },
} as const;

/* ------------------------------------------------------------- checklist */

export const CHECKLIST = {
  /** DRAFT. */
  heading: "Your Instagram accounts",
  loading: "Loading your accounts…",
  empty: "No Instagram accounts on this workspace.",
  progress: (done: number, total: number): string =>
    `${done} of ${total} updated`,
  /**
   * TICKET (SKE-4682). Three states, and NOTHING NUMERIC on any of them. The
   * bank appends "· {n} scheduled posts affected" to the first row; Hugh
   * removed the count on 2026-09-12 and the state carries the row on its own.
   */
  needsUpdating: "Needs updating",
  updated: (context: CampaignPageCopyContext): string => {
    const cutover = formatCutover(context.cutoverAt, context.timeZone);
    return cutover ? `Updated, ready for ${cutover}` : "Updated";
  },
  notCovered:
    "Not updated yet, this account is under a different Facebook profile",
  /**
   * DRAFT. A FOURTH state, and it exists only while a pass is running.
   *
   * It is never stored and never comes from the server: the contract has three
   * row states and this is not one of them. It is what a row that the pass has
   * not reached yet shows instead of "Needs updating", so the customer can see
   * the list working through rather than sitting still. The moment the pass
   * settles, every row is back to one of the server's three.
   */
  updating: "Updating…",
  /**
   * DRAFT. SKE-4707. Why this row needs updating, under the row's name.
   *
   * Hugh, 2026-09-12: "any accounts with issues should show on the page and
   * ideally say why they need reconnecting." Before this, every outstanding row
   * said only "Needs updating", which is true and unhelpful: measured on
   * staging, four of five outstanding accounts were outstanding for a reason
   * this campaign's button does not address.
   *
   * Keyed by the FIRST blocker the server lists, because that list is already
   * ordered by what a human would fix first (`capability.ts`).
   *
   * `no_business_account` is the one that earns its place. Pressing Update now
   * does not link an Instagram account to a Facebook Page; only the customer
   * can, in Instagram. So that row says what to do and in what order, rather
   * than sending them round the authorisation loop to no effect.
   *
   * VOCABULARY. SKE-4502 bans "reconnect", "scope", "permission set", "token"
   * and "migration" from every customer-facing surface in this campaign, so
   * none of these say them. "Disconnected" is not on the list and is already
   * what the rest of the product calls that state, so it stays.
   *
   * `publishing_disabled` is deliberately absent: that is a Sked-side switch,
   * not something the customer did or can fix, and naming it would invite a
   * support contact about a decision we made.
   */
  reasons: {
    no_business_account:
      "Not linked to a Facebook Page. Link it in Instagram first, then update here.",
    missing_publish_scope:
      "Sked Social needs your approval to publish to this account.",
    needs_publish_reconnect:
      "Sked Social needs your approval to publish to this account.",
    no_access_token: "This account is disconnected.",
  } as Record<string, string>,
} as const;

/**
 * SKE-4707. The one reason a row shows, or nothing.
 *
 * Takes the first blocker that has customer-facing copy rather than the first
 * blocker outright, so an unmapped or deliberately unmapped one (see
 * `publishing_disabled`) falls through to the next real reason instead of
 * blanking the line.
 */
export function checklistRowReason(
  blockers: readonly string[] | undefined
): string | undefined {
  if (!blockers) return undefined;
  for (const blocker of blockers) {
    const reason = CHECKLIST.reasons[blocker];
    if (reason) return reason;
  }
  return undefined;
}

export const PAGES_GROUP = {
  /** DRAFT. */
  heading: "Your Facebook Pages",
  /**
   * DRAFT, and the sentence that keeps the group honest. Facebook has no
   * deadline: a Page keeps publishing on what it already has, through the
   * cutover and past it. So this group is never red, never counted and never
   * carries a date, and the note says so before a customer asks.
   */
  note: "These refresh at the same time. Nothing to do, and no deadline.",
  /**
   * DRAFT. TWO STATES, AND ONLY EVER TWO. The contract deliberately sends no
   * field that distinguishes "not reached yet" from "refresh failed", because
   * to a customer those are the same thing and the page has no copy for a
   * failure that costs them nothing.
   */
  pending: "Refreshes when you update",
  refreshed: "Refreshed",
} as const;

/* -------------------------------------------------------------- timeline */

/** The three details are APPROVED (SKE-5358, 2026-10-16); the heading and
 *  labels are DRAFT. Small and plain: it exists so the customer can see
 *  there is an after, not to frighten them. */
export const TIMELINE = {
  heading: "What happens when",
  today: {
    label: "Today",
    detail: "Updated or not, every account keeps publishing as it does today",
  },
  cutover: {
    label: (context: CampaignPageCopyContext): string =>
      formatCutover(context.cutoverAt, context.timeZone) || "The switchover",
    detail:
      "Last day to update. Accounts not updated stop publishing to Instagram",
  },
  after: {
    label: "After",
    detail:
      "Updated accounts keep publishing, with the new features switched on",
  },
} as const;

/* --------------------------------------------------------------- handoff */

export const HANDOFF = {
  /** TICKET (SKE-4687), verbatim, and TWO buttons. */
  heading: "Not the person who manages Facebook access?",
  /** DRAFT. */
  body: "Send them the link and they can do it in about a minute.",
  sendThemThis: "Send them this",
  talkToSupport: "Talk to support",
  /**
   * DRAFT. The message a customer hands to whoever holds the Facebook access.
   * SKE-4687 sets its content: what is needed, by when, and that it takes
   * about a minute. Plain text with no formatting, because it has to survive
   * a chat window, a ticket and an email equally, which is also why it is
   * copied rather than opened in a mail client.
   *
   * The deadline sentence drops out entirely when no date is configured,
   * rather than leaving a hole where the date should be.
   */
  message: (context: CampaignPageCopyContext, url: string): string => {
    const cutover = formatCutover(context.cutoverAt, context.timeZone);
    const deadline = cutover
      ? ` Accounts not updated by ${cutover} will stop publishing to Instagram.`
      : "";
    return (
      "Can you update our Instagram publishing in Sked Social? It needs someone with " +
      `Facebook access and takes about a minute.${deadline} Here is the link: ${url}`
    );
  },
  copied: "Message copied. Paste it wherever they will see it.",
  copyFailed:
    "Copying is blocked in this browser. Select the message below and copy it.",
} as const;

/* --------------------------------------------------------------- support */

export const SUPPORT = {
  /** BANK. */
  prompt: "Need a hand?",
  link: "Talk to support",
  /** DRAFT. Prefills the chat so the agent starts informed. */
  chatMessage: (outstanding: number): string =>
    `I need help updating Instagram publishing for ${accountCount(outstanding)}.`,
} as const;

/* -------------------------------------------------------------- the pass */

/**
 * SKE-4687 — what the page says after, and during, an authorisation pass.
 *
 * `partial` and `failed` are TICKET copy, verbatim from SKE-4682's own "copy
 * that ships with this page" list, with two plurals corrected on the same
 * precedent as everything else in this file: the bank writes "{n} accounts
 * updated. {n} still need authorising", and a workspace with one account would
 * have read "1 accounts updated" and "1 still need authorising".
 *
 * Note which number is counted and which is bare. The first is "{n} accounts",
 * so it takes `accountCount`. The second is a bare "{n} still need", so it
 * stays bare and only its verb moves.
 *
 * NOTHING HERE SAYS WHY A ROW FAILED BEYOND WHAT FACEBOOK DID. The words
 * "scope", "permission set" and "token" are banned; "the publishing
 * permission" is the ticket's own phrase and is what ships.
 */
export const PASS = {
  /** DRAFT. Under the disabled button, so a minute of silence reads as work. */
  inProgress: "Updating your accounts with Facebook. This can take a minute.",

  /** TICKET (SKE-4682, SKE-4687). Verbatim but for the singular verb. */
  partial: (updated: number, remaining: number): string =>
    `${accountCount(updated)} updated. ${remaining} still ${
      remaining === 1 ? "needs" : "need"
    } authorising under a different Facebook account. ` +
    "Run it again with that login.",
  /** TICKET (SKE-4687). */
  authoriseAnother: "Authorise another",

  /**
   * DRAFT, and the most important line on this page for an agency.
   *
   * "Authorise another" CANNOT switch Facebook accounts, and nothing we can
   * build will make it. `fbLogin` sends `auth_type: "rerequest"`, which
   * re-opens the PERMISSION dialog for whoever is already signed in to
   * Facebook. It never opens the account chooser. Facebook's session is on
   * Facebook's origin, so we cannot clear it, and we cannot read it to warn
   * them either.
   *
   * Without this line the agency case is a silent dead end: the customer
   * presses the button, sees the same account, grants the same permissions
   * again, and the same rows stay outstanding with nothing on screen
   * explaining why. Agencies across several business portfolios are the
   * segment the strategy document says we lose when they lose their place.
   *
   * So the page says the true thing, including the part we cannot do. Two
   * rejected alternatives, both worse: `auth_type: "reauthenticate"` only
   * sometimes surfaces a chooser, so the copy would promise what we cannot
   * guarantee; and a Facebook logout link would sign them out of Facebook
   * everywhere as a side effect of our campaign.
   */
  differentLoginTitle: "Accounts under a different Facebook login?",
  differentLoginBody:
    "Sign out of Facebook in another tab, then run this again. Sked Social " +
    "cannot switch Facebook accounts for you.",

  /** TICKET (SKE-4682). Verbatim. */
  failed: (handle: string): string =>
    `We could not update ${handle}. Facebook did not grant the publishing ` +
    "permission. Try again, and make sure every box stays ticked on " +
    "Facebook's screen.",

  /**
   * APPROVED (SKE-5001, Hugh, 2026-09-21). The server's
   * `scope_not_offered`: Meta's `/me/permissions` did not list the publishing
   * permission at all, so there was no box and `failed`'s "keep every box
   * ticked" would be false. Shown once per pass rather than once per account,
   * because the cause is the signed-in Facebook profile, not the account.
   */
  notOffered:
    "Facebook did not show the publishing permission to the profile you are " +
    "signed in with, so there was nothing to tick. It is not available to " +
    "that profile yet. Try again later, or contact our support team if this " +
    "keeps happening.",

  /**
   * DRAFT. A first connect that succeeded without the publishing permission,
   * for any cause other than `notOffered` (unticked, or the read failed), so
   * it neither blames the customer nor says the connect failed.
   */
  connectedWithoutPublishing: (handle: string): string =>
    `${handle} is connected, but Instagram publishing is not switched on for ` +
    "it yet. Open Update Instagram publishing to finish it.",

  /**
   * DRAFT. Neither source has a line for the pass failing OUTRIGHT — the
   * credential exchange or the enumeration, which the server answers with a
   * 502 and no rows at all. The ticket's failure copy names a handle, and here
   * there is no handle to name because nothing was reached.
   *
   * It does not say "Facebook is down". A 502 from that route also covers an
   * expired credential and a Meta configuration problem on our side, and
   * naming the wrong cause is worse than naming none.
   */
  passFailed:
    "We could not update your accounts. Nothing changed. Try again, and if " +
    "it keeps happening we can take a look.",

  /**
   * DRAFT. The customer closed Facebook's window, or did not finish it.
   *
   * Short, and it does NOT scold. Calling the login off is a legitimate thing
   * to do and the page still needs them to come back.
   */
  abandoned:
    "Nothing changed. The Facebook window closed before it finished, so you " +
    "can start again whenever you are ready.",
} as const;

/* ------------------------------------------------ skipped accounts help */

/**
 * APPROVED (SKE-5358, Hugh, 2026-10-16). Shown while any Instagram row is
 * `not_covered`. Facebook now offers a returning customer only "Continue",
 * with no way to edit which Pages and accounts Sked Social can see, so an
 * account skipped on the first grant can only be added from Facebook's own
 * settings.
 */
export const SKIPPED_ACCOUNTS_HELP = {
  title: "Add the missing Instagram accounts",
  steps: [
    'Open "Business integrations" in Facebook and choose "View and edit" next to Sked Social.',
    "Tick the missing Pages and Instagram accounts, then Save.",
    "Come back here and press Update now.",
  ],
  button: "Open Facebook settings",
  href: "https://www.facebook.com/settings?tab=business_tools",
} as const;

/* ------------------------------------------------------- publishing lost */

/**
 * SKE-4807. What every surface says when an update went through but took the
 * publishing permission away from an account that had it.
 *
 * This is NOT `PASS.failed`. That line is for an account that never had the
 * permission and still does not; this one is for an account that HAD it, so
 * publishing through Meta's API stops working until the customer acts. The
 * Insights half of the update still landed, which is why none of these say
 * the update failed.
 *
 * Every line names BOTH causes, because the page cannot tell them apart:
 * Facebook reports the same missing permission whether the customer unticked
 * publishing on its screen or signed in with a login that does not manage the
 * account. Naming only the untick sends an agency round the same login again.
 *
 * Shown only while `instagramPublishAuthenticationRequired` is on for the
 * workspace. Same vocabulary as the rest of this file: no "reconnect", and
 * "publishing" rather than any word for what Facebook granted.
 */
export const PUBLISHING_LOST = {
  /**
   * DRAFT. One row of the pass result, beside the refused rows. Leads with the
   * outcome and the handle, so an agency scanning a list of them finds theirs
   * before reading the why.
   */
  passRow: (handle: string): string =>
    `Publishing was not kept for ${handle}. Either publishing was unticked ` +
    "on Facebook's screen, or this Facebook login does not manage it. Run it " +
    "again with the login that manages it, and keep publishing ticked.",

  /**
   * DRAFT. The status beside one account in the Update Facebook permissions
   * dialog. Short, because it sits inline in a list; "Connected" is the word
   * the dialog's own success status already uses, so the customer reads it as
   * that status with a catch rather than a new state to decode.
   */
  dialogStatus: "Connected, but publishing was not kept",

  /**
   * DRAFT. One note under the dialog's list, however many rows lost it, so a
   * fifty-account agency reads the advice once rather than fifty times.
   */
  dialogAdvice:
    "Publishing was not kept for the accounts marked above. Either publishing " +
    "was unticked on Facebook's screen, or this Facebook login does not " +
    "manage them. Run it again with the login that manages them, and keep " +
    "publishing ticked.",

  /**
   * DRAFT. Replaces the success toast after a single account's update. Says
   * "connected" first because that part is true and the customer just watched
   * it happen; saying it failed would send them to support about Insights that
   * work.
   */
  toast: (name: string): string =>
    `${name} is connected, but publishing to Instagram was not kept. Either ` +
    "publishing was unticked on Facebook's screen, or this Facebook login " +
    "does not manage it. Run it again with the login that manages it, and " +
    "keep publishing ticked.",

  /**
   * DRAFT. Appended to the toast only while the campaign is on, because the
   * campaign page is closed to everyone else and pointing at it would bounce
   * them. Text rather than a link: toasts render outside the router, so a link
   * would reload the whole app. Names the page by its eyebrow rather than the
   * menu entry, whose label changes to "Action required" in week 3.
   */
  toastCampaignPointer:
    " The Instagram publishing page lists every account that still needs it.",
} as const;

/* ------------------------------------------------------------------ rail */

export const RAIL = {
  /** DRAFT throughout. */
  heading: (outstanding: number): string =>
    outstanding === 0
      ? "Every account is updated"
      : `${accountCount(outstanding)} to update`,
  allDone:
    "Nothing left to do. Your accounts publish through the new way already.",
  primary: "Update now",

  /**
   * DRAFT. The rail says the DATE, not the whole consequence sentence.
   *
   * The hero states the consequence in full and weighted, and the timeline
   * states it a third time in its own words. Repeating the entire sentence
   * inside a card that is on screen at the same time as one of them made the
   * page read as though it had forgotten what it just said.
   *
   * The rail still has to carry the date, because it is the element that
   * follows the reader down the page after the hero has scrolled away, and a
   * deadline nobody can see once they start reading is not a deadline. So it
   * keeps the fact and drops the sentence.
   */
  deadline: (cutover: string): string => `Deadline: ${cutover}`,
} as const;
